import { useMemo } from 'react';

import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { PRODUCT_TYPES, getProductTypeById } from '@/services/getAlgoliaExperts';
import { segmentClient } from '@/util/analytics';

type ProductTypeComboboxProps = {
  className?: string;
  productTypeId: string;
  setProductTypeId: (value: string) => void;
  selectedProductTypeLabel: string;
  colors: { typography: Record<number, string> };
  bottom: number;
};

function track(event: string, properties?: Parameters<typeof segmentClient.track>[1]) {
  Promise.resolve(
    properties ? segmentClient.track(event, properties) : segmentClient.track(event)
  ).catch(() => undefined);
}

/**
 * The directory's treatment type dropdown, drawn with the same combobox as the cancer type one
 * beside it so the two cannot differ in layout, padding, keyboard behavior or clearing. It maps
 * the treatment types onto the picker and reports the treatment type analytics events.
 */
export function ProductTypeCombobox({
  className,
  productTypeId,
  setProductTypeId,
  selectedProductTypeLabel,
  colors,
  bottom,
}: ProductTypeComboboxProps) {
  const options = useMemo<ComboboxOption[]>(
    () =>
      PRODUCT_TYPES.map((productType) => ({
        value: productType.id,
        label: productType.label,
        synonyms: [],
      })),
    []
  );

  const analytics = useMemo(
    () => ({
      onViewed: () => track(ANALYTICS_EVENTS.PRODUCT_TYPE_VIEWED),
      onSelected: (selectedId: string) =>
        track(ANALYTICS_EVENTS.PRODUCT_TYPE_SELECTED, {
          product_type: getProductTypeById(selectedId)?.label || '',
        }),
    }),
    []
  );

  return (
    <Combobox
      className={className}
      value={productTypeId}
      onChange={setProductTypeId}
      options={options}
      selectedLabel={selectedProductTypeLabel}
      textColor={colors.typography[900]}
      bottomInset={bottom}
      showSearchIcon={false}
      placeholder="Select treatment type"
      label="Select treatment type"
      searchPlaceholder="Search treatment types"
      clearLabel="Clear filter, show all treatment types"
      noOptionsLabel="No treatment types found"
      clearSearchLabel="Clear treatment type search"
      optionsLabel="Treatment type options"
      listboxTestId="product-type-listbox"
      analytics={analytics}
    />
  );
}
